import type { Color, PaletteOptions } from '@mui/material';

type ColorsKey = 'red' | 'yellow' | 'green' | 'blue' | 'purple' | 'neutral';

type ColorValue = {
    [key in keyof Color]: string;
};

type EvaneosColorsType = {
    [key in ColorsKey]: Partial<ColorValue>;
};

export const colors: EvaneosColorsType = {
    red: {
        100: '#FFECE6',
        200: '#FEC9B9',
        300: '#FCA288',
        400: '#F88762',
        500: '#F56E42',
        600: '#D43F11',
        700: '#A23210',
        800: '#71240E',
        900: '#4A1A0C',
    },
    yellow: {
        100: '#FFF6E4',
        200: '#FFE9BD',
        300: '#FFDB92',
        400: '#FFCC67',
        500: '#FFC042',
        600: '#FFAD0A',
        700: '#E59900',
        800: '#BD7E00',
        900: '#8A5C00',
    },
    green: {
        100: '#E2F2EE',
        200: '#B4E2D8',
        300: '#84D0BF',
        400: '#54BCA6',
        500: '#169E82',
        600: '#0F8066',
        700: '#00543C',
        800: '#003526',
        900: '#00251B',
    },
    blue: {
        100: '#E3F2FD',
        200: '#BBDEFB',
        300: '#90CAF9',
        400: '#64B5F6',
        500: '#42A5F5',
        600: '#0D8DF2',
        700: '#0A6CBD',
        800: '#064079',
        900: '#052557',
    },
    purple: {
        100: '#F0EAFB',
        200: '#CEBAF2',
        300: '#AB8BE8',
        400: '#895CDF',
        500: '#672CD6',
        600: '#5223AB',
        700: '#3E1A80',
        800: '#291256',
        900: '#15092B',
    },
    neutral: {
        0: '#FFFFFF',
        100: '#F6F7F8',
        200: '#E9EBEC',
        300: '#CFD4D8',
        400: '#ABB2BA',
        500: '#9199A1',
        600: '#6F7780',
        700: '#585F66',
        800: '#41474D',
        900: '#1F2226',
    },
} as const;

export const palette: PaletteOptions = {
    primary: {
        100: colors.green[100],
        200: colors.green[200],
        300: colors.green[300],
        400: colors.green[400],
        500: colors.green[500],
        600: colors.green[600],
        700: colors.green[700],
        800: colors.green[800],
        900: colors.green[900],
        light: colors.green[600],
        main: colors.green[700],
        dark: colors.green[800],
    },
    secondary: {
        100: colors.neutral[100],
        200: colors.neutral[200],
        300: colors.neutral[300],
        400: colors.neutral[400],
        500: colors.neutral[500],
        600: colors.neutral[600],
        700: colors.neutral[700],
        800: colors.neutral[800],
        900: colors.neutral[900],
        light: colors.neutral[600],
        main: colors.neutral[900],
    },
    error: {
        100: colors.red[100],
        200: colors.red[200],
        300: colors.red[300],
        400: colors.red[400],
        500: colors.red[500],
        600: colors.red[600],
        700: colors.red[700],
        800: colors.red[800],
        900: colors.red[900],
        light: colors.red[500],
        main: colors.red[600],
        dark: colors.red[700],
    },
    warning: {
        100: colors.yellow[100],
        200: colors.yellow[200],
        300: colors.yellow[300],
        400: colors.yellow[400],
        500: colors.yellow[500],
        600: colors.yellow[600],
        700: colors.yellow[700],
        800: colors.yellow[800],
        900: colors.yellow[900],
        light: colors.yellow[500],
        main: colors.yellow[600],
        dark: colors.yellow[700],
    },
    info: {
        100: colors.blue[100],
        200: colors.blue[200],
        300: colors.blue[300],
        400: colors.blue[400],
        500: colors.blue[500],
        600: colors.blue[600],
        700: colors.blue[700],
        800: colors.blue[800],
        900: colors.blue[900],
        light: colors.blue[500],
        main: colors.blue[600],
        dark: colors.blue[700],
    },
    success: {
        100: colors.green[100],
        200: colors.green[200],
        300: colors.green[300],
        400: colors.green[400],
        500: colors.green[500],
        600: colors.green[600],
        700: colors.green[700],
        800: colors.green[800],
        900: colors.green[900],
        light: colors.green[600],
        main: colors.green[700],
        dark: colors.green[800],
    },
    text: {
        primary: colors.neutral[900],
        secondary: colors.neutral[700],
        disabled: colors.neutral[300],
        contrast: colors.neutral[0],
        action: colors.green[700],
        success: colors.green[800],
        information: colors.blue[800],
        errorDefault: colors.red[600],
        error: colors.red[800],
        warning: colors.yellow[900],
        ai: colors.purple[800],
    },
    background: {
        main: colors.neutral[0],
        variant: colors.neutral[100],
        highlight: colors.green[700],
        tertiary: colors.neutral[900],
        contrasted: colors.green[900],
        information: colors.blue[100],
        success: colors.green[100],
        error: colors.red[100],
        warning: colors.yellow[100],
        ai: colors.purple[100],
    },
    border: {
        main: colors.neutral[300],
        hovered: colors.neutral[500],
        disabled: colors.neutral[200],
        contrast: colors.neutral[0],
        selected: colors.green[700],
        error: colors.red[600],
    },
    icon: {
        primary: colors.neutral[900],
        hovered: colors.neutral[500],
        disabled: colors.neutral[200],
        contrast: colors.neutral[0],
        selected: colors.green[700],
        error: colors.red[600],
    },
};

// Extend the PaletteBackground interface
declare module '@mui/material/styles' {
    interface TypeBackground {
        main: string;
        variant: string;
        highlight: string;
        tertiary: string;
        contrasted: string;
        information: string;
        success: string;
        error: string;
        warning: string;
        ai: string;
    }
    interface Color {
        0: string;
    }

    interface TypeText {
        contrast: string;
        action: string;
        success: string;
        information: string;
        errorDefault: string;
        error: string;
        warning: string;
        ai: string;
    }

    interface TypeBorder {
        main: string;
        hovered: string;
        disabled: string;
        contrast: string;
        selected: string;
        error: string;
    }

    interface TypeIcon {
        primary: string;
        hovered: string;
        disabled: string;
        contrast: string;
        selected: string;
        error: string;
    }

    interface Palette {
        background: TypeBackground;
        text: TypeText;
        border: TypeBorder;
        icon: TypeIcon;
        neutral: {
            0: string;
        };
    }

    interface PaletteOptions {
        background?: Partial<TypeBackground>;
        text?: Partial<TypeText>;
        border?: Partial<TypeBorder>;
        icon?: Partial<TypeIcon>;
        neutral?: {
            0: string;
        };
    }
}
